/*
 * Copyright 2022 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import {
    formatDateAndTimeV1,
    NotificationTypeV1,
} from "@startree-ui/platform-ui";
import { DateTime } from "luxon";

export const isValidJSONString = (data?: string): boolean => {
    if (data) {
        return Boolean(getJSONFromString(data));
    }

    return false;
};

/**
 * * Converts `date` to ISO-Format(Feb 08 2022 00:00 AM)
 *
 * @param date should be string in EPOCH_MILLIS(1524252) or ISO_FORMAT(2022-08-10)
 * @returns formatted date in valid ISO-Format
 * or empty string(if date string is not valid).
 */
export const getFormattedDate = (date: string): string => {
    if (!date) {
        return "";
    }
    let formattedDate = "";

    if (isNaN(Number(date))) {
        const isoDate = DateTime.fromISO(date);
        if (!isoDate.isValid) {
            return date;
        }
        formattedDate = formatDateAndTimeV1(isoDate.toMillis());
    } else {
        formattedDate = formatDateAndTimeV1(Number(date));
    }

    return formattedDate;
};

export const getJSONFromString = <T = Record<string, unknown>>(
    data: string
): T | null => {
    try {
        // Format string if possible and return valid JSON
        return JSON.parse(data);
    } catch (e) {
        // Invalid JSON, return null
        return null;
    }
};

export const escapeSpecialChars = (data?: string): string => {
    return data
        ? data
              .replace(/[\\]/g, "\\")
              .replace(/[/]/g, "/")
              .replace(/[\b]/g, "\b")
              .replace(/[\f]/g, "\f")
              .replace(/[\n]/g, "\n")
              .replace(/[\t]/g, "\t")
              .replace(/["]/g, '"')
        : "";
};

export const prettyPrintJsonString = (data?: string): string | undefined => {
    if (data && isValidJSONString(data)) {
        return JSON.stringify(JSON.parse(data), null, 2);
    }

    return undefined;
};

export function shiftValueFromArrays<T>(
    value: T,
    obj: Record<string, T[]>,
    keys: string | Array<string>
): Record<string, T[]> {
    const retObj: Record<string, T[]> = {};
    Object.keys(obj).forEach((k: string) => {
        retObj[k] = obj[k].includes(value)
            ? obj[k].filter((v) => {
                  return v !== value;
              })
            : obj[k];
    });
    if (typeof keys === "string") {
        retObj[keys] = [...retObj[keys], value];
    } else {
        for (const key of keys) {
            retObj[key] = [...retObj[key], value];
        }
    }

    return retObj;
}

export const getSeverity = (type: string): NotificationTypeV1 => {
    const severity = type.toLowerCase();

    switch (severity) {
        case "info":
            return NotificationTypeV1.Info;
        case "warning":
            return NotificationTypeV1.Warning;
        case "warn":
            return NotificationTypeV1.Warning;
        case "success":
            return NotificationTypeV1.Success;
        default:
            return NotificationTypeV1.Error;
    }
};
